import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { Point, Polygon } from 'geojson';
import { map } from 'rxjs/operators';
import { FlyFreelyConstants } from '../constants';
import {
    LocationSummary,
    MissionCriteria,
    MissionDto,
    MissionRoleDto,
    MissionSummaryDto
} from '../model/api';
import { GqlFilterField } from './interfaces';
import { httpParamSerializer } from './service.helpers';

export interface MissionLocationPoint {
    id: number;
    name: string;
    representativePoint: Point;
}

export interface MissionLocation {
    id: number;
    name: string;
    representativePoint: Point;
    boundary: Polygon;
    missionList: {
        id: number;
        name: string;
        status: MissionDto.Status;
        missionDate: string;
        missionEstimatedTime: number;
        timeZone: string;
        missionCrew: {
            person: {
                firstName: string;
                lastName: string;
            };
            missionRole: {
                coreRole?: MissionRoleDto.CoreRole;
            };
        }[];
    }[];
}

@Injectable({
    providedIn: 'root'
})
export class OperationsSummaryService {
    private baseUrl: string;

    constructor(
        constants: FlyFreelyConstants,
        private http: HttpClient,
        private apollo: Apollo
    ) {
        this.baseUrl = constants.API_URL;
    }

    findMissionsAtLocation(locationId: number) {
        return this.http.get<MissionSummaryDto[]>(
            `${this.baseUrl}/webapi/operationsSummary/missions`,
            {
                params: httpParamSerializer({ locationId })
            }
        );
    }

    findMissionLocationPoints(criteria: MissionCriteria) {
        return this.apollo
            .query<{
                findMissionLocations: MissionLocationPoint[];
            }>({
                query: gql`
                    query find(
                        $criteria: MissionCriteria
                    ) {
                        findMissionLocations(
                            criteria: $criteria
                        ) {
                            id
                            name
                            representativePoint {
                                type
                                coordinates
                            }
                        }
                    }
                `,
                variables: {
                    criteria,
                },
                fetchPolicy: 'network-only'
            })
            .pipe(map(res => res.data.findMissionLocations));
    }

    findMissionLocations(criteria: MissionCriteria) {
        return this.apollo
            .query<{
                findMissionLocations: MissionLocation[];
            }>({
                query: gql`
                    query find(
                        $criteria: MissionCriteria
                    ) {
                        findMissionLocations(
                            criteria: $criteria
                        ) {
                            id
                            name
                            representativePoint {
                                type
                                coordinates
                            }
                            boundary {
                                type
                                coordinates
                            }
                            missionList {
                                id
                                name
                                status
                                missionDate
                                missionEstimatedTime
                                timeZone
                                missionCrew {
                                    person {
                                        firstName
                                        lastName
                                    }
                                    missionRole {
                                        coreRole
                                    }
                                }
                            }
                        }
                    }
                `,
                variables: {
                    criteria,
                },
                fetchPolicy: 'network-only'
            })
            .pipe(map(res => res.data.findMissionLocations));
    }
}
